import { useEffect, useState } from 'react';

import { Button, message, Space } from 'antd';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import apiConfig from '../../API_Utils/APIUtil/ApiConfig';
import CallApi from '../../API_Utils/APIUtil/CallApi';
import { getError, validator, delay } from '../../clientUtils';
import DocumentCard from '../../Components/Common/DocumentCard';
import { FILE_TYPE_ATTRIBUTE } from '../../constants';
import AsyncBulkUpload from '../BulkUploaders/AsyncBulkUpload';
import {
  JOB_NAMES,
  POLLING_FREQ,
  UI_JOB_STATUS,
  JOB_STATUS,
  JOB_STATUS_MAPPING,
  ifButtonDisabled,
  getJobType
} from '../BulkUploaders/BulkUploaderUtils';
import ParserInfoMessage from '../BulkUploaders/ParserInfoMessage';

const onError = (error) => message.error(getError(error));

const onUpload = (jobName, file, onSuccess, setLoading) => {
  CallApi(
    apiConfig.job.getPresignedURL,
    { jobName },
    null,
    setLoading,
    async (res) => {
      try {
        const response = await axios.put(res, file, {
          headers: {
            'Content-Type': file.type
          }
        });

        if (response.status === 200) onSuccess(file, res);
      } catch {
        message.error('Failed to upload file');
      }
    },
    onError
  );
};

const getBody = (type, fileLink) => {
  const fileType = `POSI_TEXTIFY_${type}_PARSE_INPUT_FILE`;
  const baseJobName = `POSI_TEXTIFY_${type}_PARSE`;

  return {
    job_name: baseJobName,
    files: [
      {
        type: fileType,
        file_link: fileLink
      }
    ],
    metadata: {}
  };
};

const DocumentParser = () => {
  const { type, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [jobStatus, setJobStatus] = useState(UI_JOB_STATUS.uploading);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [responseFiles, setResponseFiles] = useState([]);

  const typeMap = {
    'purchase-order': 'PO',
    invoice: 'IN',
    awb: 'AWB'
  };
  const titleMap = {
    'purchase-order': 'Purchase Order',
    invoice: 'Invoice',
    awb: 'AWB'
  };

  const navigate = useNavigate();

  useEffect(() => {
    setFiles([]);
    setJobStatus(UI_JOB_STATUS.uploading);
  }, []);

  useEffect(() => {
    let pollingId;
    if (id) {
      const getJobStatus = () => {
        CallApi(
          { ...apiConfig.job.status, path: apiConfig.job.status.path(id) },
          null,
          null,
          null,
          (res) => {
            if (JOB_STATUS_MAPPING[res.status] === 'end' && pollingId) {
              clearInterval(pollingId);
            }

            setResponseFiles(res?.files);
            setJobStatus(getJobType(res.status, res.job_name, []));
          },
          onError
        );
      };
      getJobStatus();

      pollingId = setInterval(getJobStatus, POLLING_FREQ);
    }
    return () => clearInterval(pollingId);
  }, [navigate, type, id]);

  const uploadProps = {
    title: 'Document File',
    description: 'Please upload the document to parse',
    accept: FILE_TYPE_ATTRIBUTE.pdf,
    files,
    validator,
    onUpload: (file) =>
      onUpload(
        `POSI_TEXTIFY_${typeMap[type]}_PARSE`,
        file,
        (f, link) => {
          setFiles([{ ...f, fileLink: link }]);
          setJobStatus(UI_JOB_STATUS.uploaded);
        },
        setLoading
      ),
    onRemove: async () => {
      setFiles([]);
      setJobStatus(UI_JOB_STATUS.uploading);
      message.success('File deleted successfully!');
    },
    deleteDisabled: [UI_JOB_STATUS.submitted, UI_JOB_STATUS.submitting, UI_JOB_STATUS.validating].includes(jobStatus)
  };

  const fileUploaderProps = {
    message: '',
    items: [uploadProps]
  };

  const handleClick = () => {
    CallApi(
      apiConfig.documentParser.parse,
      null,
      getBody(typeMap[type], files?.[0]?.fileLink),
      setButtonLoading,
      (res) => navigate(`/document-parser/${type}/${res.job_id}`),
      onError
    );
  };

  return (
    <AsyncBulkUpload
      after={
        <Space>
          {responseFiles?.map(({ type, fileLink }) => (
            <DocumentCard link={fileLink} name={type} />
          ))}
        </Space>
      }
      button={
        <Button
          disabled={ifButtonDisabled(jobStatus, files?.validationSummary)}
          loading={buttonLoading}
          onClick={handleClick}
          type="primary">
          Parse
        </Button>
      }
      info={<ParserInfoMessage status={jobStatus} />}
      menuKey={`bu_ro_${type}`}
      title={`${titleMap[type]} Parser`}
      uploadersProp={[fileUploaderProps]}
    />
  );
};

export default DocumentParser;
